import {
    ChangeDetectorRef, Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import {FUIAbstractInput} from "../../framework/abstract-input";
import {FUIOptionsService, SelectionsEvent} from "../../framework/options.service";
import {Subscription} from "rxjs";
import {FUIEventsService} from "../../../events/events.service";

@Directive()
export abstract class FUIAbstractMultiselectInput extends FUIAbstractInput implements OnDestroy, OnInit {
    @Input() public field: string;
    @Input() public filterable: boolean;
    @Output() public filtered$: EventEmitter<string>;
    public filteredOptions: Array<any>;
    @Input() public optionOpenable: boolean;
    @Output() public optionOpened$: EventEmitter<any>;
    public selected: string;
    @Input() public total: number;
    private _$click: Subscription;
    private _selections: any[];
    private _options: Array<any>;
    private _popped: boolean;
    private _query: string;
    private clicking = false;

    constructor(changeDetectorRef: ChangeDetectorRef,
                public elementRef: ElementRef,
                protected eventsService: FUIEventsService,
                protected optionsService: FUIOptionsService) {
        super(changeDetectorRef);
        this.added$ = new EventEmitter<boolean>();
        this.extended$ = new EventEmitter<boolean>();
        this.optionOpened$ = new EventEmitter<any>();
        this.filterable = true;
        this.filtered$ = new EventEmitter<string>();
        this.options = new Array<any>();
        this.filteredOptions = new Array<any>();
    }

    private _key: string;
    private _summaryKey: string;

    /**
     *
     * @returns {string}
     */
    get key(): string {
        return this._key;
    }

    /**
     *
     * @param {string} key
     */
    @Input()
    set key(key: string) {
        if (this._key !== key) {
            this._key = key;

            if (this.value) {
                this.selections = this.value;
            }
        }
    }

    /**
     *
     * @returns {string}
     */
    get summaryKey(): string {
        return this._summaryKey;
    }

    /**
     *
     * @param {string} key
     */
    @Input()
    set summaryKey(summaryKey: string) {
        if (this._summaryKey !== summaryKey) {
            this._summaryKey = summaryKey;

            if (this.value) {
                this.selections = this.value;
            }
        }
    }

    /**
     *
     * @returns {Array<any>}
     */
    get options(): Array<any> {
        return this._options;
    }

    /**
     *
     * @param {Array<any>} options
     */
    @Input()
    set options(options: Array<any>) {
        if (options) {
            if (options.length != undefined) {
                this._options = options;
                this.filteredOptions = this.filterSelectedOptions(this._options);
            }
        }
    }

    /**
     *
     * @returns {boolean}
     */
    get popped(): boolean {
        return this._popped;
    }

    /**
     *
     * @param {boolean} popped
     */
    set popped(popped: boolean) {
        this._popped = popped;
        this.focussed = popped;
        this.focussed$.emit(this.focussed);
    }

    /**
     *
     * @returns {string}
     */
    get query(): string {
        return this._query;
    }

    /**
     *
     * @param {string} query
     */
    set query(query: string) {
        if (this.query !== query) {
            this._query = query;

            if (this.filterable) {
                this.filteredOptions = this.filterSelectedOptions(this._options);
            } else {
                this.filtered$.emit(this._query);
            }
        }
    }

    /**
     *
     * @returns {any}
     */
    get selections(): any[] {
        return this._selections;
    }

    /**
     *
     * @param selections
     */
    set selections(selections: any[]) {
        this._selections = selections;

        if (this.key) {
            this.selected = this._selections.map((selection: any) => selection[this.summaryKey || this.key]).join(", ");
            this.filteredOptions = this.filterSelectedOptions(this._options);
        }
    }

    /**
     *
     * @returns {string}
     */
    override get value(): any {
        return this._value;
    }

    /**
     *
     * @param value
     */
    @Input()
    override set value(value: any) {
        super.value = value;
        this.selections = value;
    }

    ngOnInit(): void {
        this._$click = this.eventsService.click$.subscribe(
            (click) => {
                if (!this.clicking && this.popped && !this.elementRef.nativeElement.contains(click.target)) {
                    this.popped = false;
                }
            }
        )
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._$click.unsubscribe();
    }

    filterSelectedOptions(options: any[]): any[] {
        return options.filter((option) => !this.isOptionSelected(option) && (!this.filterable || !this._query ||
            option[this.key].toLowerCase().includes(this._query.toLowerCase())));
    }

    isOptionSelected(option: any): boolean {
        if (this.selections != undefined) {
            return this.selections.find((selection) => selection[this.field] === option[this.field]) != undefined;
        } else {
            return false;
        }
    }

    /**
     *
     */
    public add(): void {
        this.added$.emit(true);
    }

    /**
     *
     */
    public override click(): void {
        if (!this.clicking) {
            this.clicking = true;
            setTimeout(() => this.clicking = false);
            this.focus();
            this.popped = !this.popped;
        }
    }

    /**
     * If a user presses enter or space while focussed on the element,
     * use the click method to apply the relevant interaction
     * @param {KeyboardEvent} keyboardEvent
     */
    @HostListener("keypress", ["$event"])
    public keypress(keyboardEvent: KeyboardEvent) {
        if (!this.disabled) {
            if (keyboardEvent.code === "Enter" || keyboardEvent.code === "Space" || keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                this.click();
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public empty(options: Array<any>): boolean {
        return !this.loading && (!options || options.length === 0);
    }

    /**
     * Emit an event when the extended search button
     * is clicked
     */
    public extend(): void {
        this.extended$.emit(true);
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public filled(options: Array<any>): boolean {
        return options && options.length > 0;
    }

    /**
     * Check if any of the items required for the
     * floating tool bar are included to decide
     * whether or not to show it
     * @returns {boolean}
     */
    public ftb(): boolean {
        return !this.loading && (this.addable || (this.options.length > 0 && (this.extendable || this.limited())));
    }

    /**
     * Check if the returned options count
     * is less than the total number of items available.
     * @returns {boolean}
     */
    public limited(): boolean {
        return this.total && this.options && this.options.length < this.total;
    }

    /**
     * Checks if the list is filterable based on
     * it's length
     * @returns {boolean}
     */
    public searchable(): boolean {
        return this.options && (!this.filterable || this.options.length >= 10);
    }

    /**
     *
     * @param {boolean} popped
     */
    public pop(popped: boolean): void {
        this.popped = popped;
    }

    /**
     * Assign the selected values
     * @param event
     */
    select(event: SelectionsEvent): void {
        if (event.selections) {
            this.value = event.selections;
        }
    }

    /**
     * Opens a currently selected option
     * @param {Event} event
     * @param option
     */
    openOption(event: Event, option: any) {
        event.stopPropagation();
        this.optionOpened$.next(option);
    }

    /**
     *
     * @param value
     */
    public override writeValue(value: any): void {
        this._value = value;

        if (this._value) {
            this.valued = true;
            this.selections = value;
            this.optionsService.selections = this._value;
            this.filteredOptions = this.filterSelectedOptions(this._options);
            if (!this.destroyed) {
                this.changeDetectorRef.detectChanges();
            }
        } else if (this.valued) {
            this.valued = false;
            this.selections = [];
            this.optionsService.selections = [];
            this.filteredOptions = this.filterSelectedOptions(this._options);
            if (!this.destroyed) {
                this.changeDetectorRef.detectChanges();
            }
        }
    }

    clearSelection(mouse: MouseEvent): void {
        mouse.stopPropagation();
        this.value = [];
        this.optionsService.selections = this._value;
    }
}
